/**
 * ComponenteEditorMinuta - Editor WYSIWYG (Tiptap) para Minutas
 *
 * CONTEXTO DE NEGÓCIO:
 * Permite que o advogado edite a "minuta recomendada" gerada pela IA
 * diretamente no navegador, com ferramentas de formatação básicas.
 * As alterações são persistidas no backend (TAREFA-087) via endpoint
 * PATCH /api/processos/{processo_id}/minuta, usando operações de edição.
 *
 * IMPLEMENTAÇÃO:
 * - Usa Tiptap (StarterKit + Underline) como editor rich text
 * - Converte o conteúdo Markdown (persistido no backend) em HTML para o editor
 *   usando 'marked'; e, a cada alteração, converte o HTML do editor para
 *   Markdown com 'turndown' para persistir.
 * - Debounce de 800ms para salvar alterações e evitar chamadas excessivas
 * - Barra de ferramentas com botões de formatação básicos
 *
 * DECISÕES:
 * - Persistência via operação única "replace" de todo o conteúdo (0..len)
 *   pela simplicidade e robustez; futuras otimizações podem enviar diffs reais
 *   se necessário.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import type { Editor } from '@tiptap/core';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { Bold, Italic, UnderlineIcon, List, ListOrdered, RotateCcw, RotateCw, Heading1, Heading2, Heading3, Save, Loader2 } from 'lucide-react';
import { editarMinuta, obterProcesso, OperacaoEdicaoMinuta } from '../../servicos/servicoApiProcessos';
import { criarChatSocket } from '../../servicos/servicoChatSocket';

interface ComponenteEditorMinutaProps {
  processoId: string;
  idMinuta?: string;
  className?: string;
}

export function ComponenteEditorMinuta({ processoId, idMinuta, className }: ComponenteEditorMinutaProps): JSX.Element {
  const [carregando, setCarregando] = useState<boolean>(true);
  const [salvando, setSalvando] = useState<boolean>(false);
  const [erro, setErro] = useState<string>('');

  // Conteúdo markdown atual (persistido/último salvo)
  const markdownAtualRef = useRef<string>('');
  const idMinutaAtualRef = useRef<string | undefined>(idMinuta);
  const debounceTimeoutRef = useRef<number | undefined>(undefined);

  // Conversor HTML->Markdown
  const turndown = useMemo(() => new TurndownService({ headingStyle: 'atx' }), []);

  // Editor Tiptap
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        history: {},
      }),
      Underline,
    ],
    content: '<p>Carregando minuta...</p>',
    immediatelyRender: false,
    onUpdate: ({ editor }: { editor: Editor }) => {
      // Debounced save on content change
      const html = editor.getHTML();
      if (debounceTimeoutRef.current) window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = window.setTimeout(() => salvar(html), 800);
    },
  });

  // Save handler
  const salvar = async (html: string) => {
    try {
      if (!processoId) return;
      setSalvando(true);
      const novoMarkdown = turndown.turndown(html);

      // Construir operação de replace global (0..len)
      const anterior = markdownAtualRef.current || '';
      const operacoes: OperacaoEdicaoMinuta[] = [
        {
          tipo: 'replace',
          offset_inicio: 0,
          offset_fim: anterior.length,
          novo_conteudo: novoMarkdown,
        },
      ];

      const resp = await editarMinuta(processoId, operacoes, idMinutaAtualRef.current);
      // Atualizar cache local com o markdown confirmado pelo backend
      markdownAtualRef.current = resp.data?.conteudo_markdown ?? novoMarkdown;
      setSalvando(false);
    } catch (e: unknown) {
      setSalvando(false);
      console.warn('[ComponenteEditorMinuta] Falha ao salvar minuta:', e);
      setErro('Não foi possível salvar alterações da minuta.');
    }
  };

  // Carregar conteúdo inicial da minuta (recomendada ou a idMinuta específica)
  useEffect(() => {
    (async () => {
      try {
        setCarregando(true);
        setErro('');
  const resp = await obterProcesso(processoId);
  const processo = resp.data; // ProcessoApi
  const metadados = (processo?.metadados as Record<string, unknown>) || {};
  const resultado = (metadados.resultado_analise as Record<string, unknown>) || {};
  const conjunto = (resultado.conjunto_minutas as Record<string, unknown>) || {};
  const lista = (conjunto.minutas as Array<{ id_minuta?: string; conteudo_markdown?: string }>) || [];
  const alvoId: string | undefined = idMinuta || (conjunto.minuta_recomendada_id as string | undefined);

        let markdown = '';
        let idEscolhida: string | undefined = alvoId;
        if (alvoId) {
          const found = lista.find(m => (m || {}).id_minuta === alvoId);
          if (found) markdown = found.conteudo_markdown || '';
        }
        // Se não achou por id, usa a primeira
        if (!markdown && lista.length > 0) {
          markdown = (lista[0]?.conteudo_markdown) || '';
          idEscolhida = lista[0]?.id_minuta;
        }

        // Atualiza refs de estado
        idMinutaAtualRef.current = idEscolhida;
        markdownAtualRef.current = markdown || '';

        // Converte markdown->HTML e carrega no editor
        const html = marked.parse(markdownAtualRef.current || '');
        editor?.commands.setContent(html || '<p></p>', false);
  } catch (e: unknown) {
        console.error('[ComponenteEditorMinuta] Erro ao carregar processo/minuta:', e);
        setErro('Não foi possível carregar a minuta do processo.');
      } finally {
        setCarregando(false);
      }
    })();
  }, [processoId, idMinuta, editor]); // include `editor` so we set content once the editor instance is ready

  // Assinar eventos de atualização de documento via WebSocket do processo
  useEffect(() => {
    if (!processoId) return;
    const conexao = criarChatSocket(processoId, {
      onAtualizacaoMinuta: (evento) => {
        try {
          const markdownNovo = (evento?.conteudo_markdown as string) || '';
          if (markdownNovo && markdownNovo !== markdownAtualRef.current) {
            markdownAtualRef.current = markdownNovo;
            const html = marked.parse(markdownNovo || '');
            // Atualizar conteúdo sem disparar onUpdate (evita loop de salvar)
            editor?.commands.setContent(html || '<p></p>', false);
          }
        } catch (err) {
          console.warn('[ComponenteEditorMinuta] Falha ao aplicar atualização de minuta via WS:', err);
        }
      },
    });
    return () => {
      try { conexao.fechar(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processoId]);

  // Toolbar actions helpers
  const toggleBold = () => editor?.chain().focus().toggleBold().run();
  const toggleItalic = () => editor?.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor?.chain().focus().toggleUnderline().run();
  const setH1 = () => editor?.chain().focus().toggleHeading({ level: 1 }).run();
  const setH2 = () => editor?.chain().focus().toggleHeading({ level: 2 }).run();
  const setH3 = () => editor?.chain().focus().toggleHeading({ level: 3 }).run();
  const bulletList = () => editor?.chain().focus().toggleBulletList().run();
  const orderedList = () => editor?.chain().focus().toggleOrderedList().run();
  const undo = () => editor?.chain().focus().undo().run();
  const redo = () => editor?.chain().focus().redo().run();

  return (
    <div className={"w-full border border-gray-200 rounded-lg shadow-sm bg-white " + (className || '')}>
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-3">
        <div className="font-semibold text-gray-800">Editor de Minuta</div>
        <div className="flex items-center gap-3 text-sm">
          {salvando ? (
            <div className="inline-flex items-center gap-2 text-primary-700">
              <Loader2 className="size-4 animate-spin" /> Salvando...
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 text-gray-500">
              <Save className="size-4" /> Alterações salvas
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b bg-white px-3 py-2">
        <button onClick={toggleBold} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('bold') ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Negrito">
          <Bold className="size-4" />
        </button>
        <button onClick={toggleItalic} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('italic') ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Itálico">
          <Italic className="size-4" />
        </button>
        <button onClick={toggleUnderline} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('underline') ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Sublinhado">
          <UnderlineIcon className="size-4" />
        </button>
        <span className="mx-1 h-5 w-px bg-gray-200"></span>
        <button onClick={setH1} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('heading', { level: 1 }) ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Título H1">
          <Heading1 className="size-4" />
        </button>
        <button onClick={setH2} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('heading', { level: 2 }) ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Título H2">
          <Heading2 className="size-4" />
        </button>
        <button onClick={setH3} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('heading', { level: 3 }) ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Título H3">
          <Heading3 className="size-4" />
        </button>
        <span className="mx-1 h-5 w-px bg-gray-200"></span>
        <button onClick={bulletList} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('bulletList') ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Lista não ordenada">
          <List className="size-4" />
        </button>
        <button onClick={orderedList} className={`inline-flex size-8 items-center justify-center rounded border transition-colors ${editor?.isActive('orderedList') ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} title="Lista ordenada">
          <ListOrdered className="size-4" />
        </button>
        <span className="mx-1 h-5 w-px bg-gray-200"></span>
        <button onClick={undo} className="inline-flex size-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50" title="Desfazer">
          <RotateCcw className="size-4" />
        </button>
        <button onClick={redo} className="inline-flex size-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50" title="Refazer">
          <RotateCw className="size-4" />
        </button>
      </div>

      {/* Editor Area */}
      <div className="p-4">
        {erro && (
          <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {erro}
          </div>
        )}
        {carregando ? (
          <div className="text-sm text-gray-500">Carregando minuta...</div>
        ) : (
          <div className="prose max-w-none">
            <EditorContent editor={editor} />
          </div>
        )}
      </div>


    </div>
  );
}
